import { resolveTheme } from '@shared/themes';
import { Injectable, inject } from '@angular/core';
import { IUserProfile } from 'app/models/user/user-profile';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { Observable, Subject } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent, IConfirmDialog } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { map, take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { ILicenseChangeDialog, InHomeCareChangeDialogComponent } from 'app/components/license/in-home-care/in-home-care-change/in-home-care-change-dialog.component';

@Injectable({ providedIn: 'root' })
export class SharedService {
  public mobile: boolean = false;
  public tablet: boolean = false;
  public development: boolean = environment.build === 'Development';
  private api: string = environment.privateApi;
  private changeTheme: Subject<string | null> = new Subject<string | null>();
  public get darkMode(): boolean {
    return resolveTheme(this.userProfile.theme).mode === 'dark';
  }

  /**
   * Color Codes
   */
  public purple: string = '#9355dc';
  public blue: string = '#e8f0f7';
  public gold: string = '#ffbe2e';
  public green: string = '#e6f0eb';
  public pink: string = '#e9695f';
  public orange: string = '#EC891D';
  public red: string = '#fc4132';
  public loading: boolean = false;
  public today: Date = new Date(Date.now());

  /**
   * License Type Names
   */
  public readonly hemoTechLicenseName = 'Hemo Tech';


  /**
   * Entity Types
   */
  public inHomeCareLicense: string = 'InHomeCareLicense';
  public inHomeCareChange: string = 'InHomeCareChange';
  public hemoTechChange: string = 'HemoTechChange';
  public hospitalLicense: string = 'HospitalLicense';
  public onCampusBuilding: string = 'OnCampusBuilding';
  public satelliteLocation: string = 'SatelliteLocation';
  public entityOwner: string = 'EntityOwner';
  public branchOperation: string = 'BranchOperation';
  public administratorApplication: string = 'IHCAdministratorApplication';
  public professionalExperience: string = 'IHCProfessionalExperience';
  public managementExperience: string = 'IHCManagementExperience';
  public classification: string = 'Classification';
  public complaint: string = 'Complaint';
  public nurse: string = 'Nurse';
  public parent: string = 'Parent';
  public address: string = 'Address';

  public applicantInformation: string = 'ApplicantInformation';
  public entityTypeLicense: string = 'LicenseMaster';

  public readableEntityTypes: { [key: string]: string } = {
    'InHomeCareLicense': 'In Home Care License',
    'InHomeCareChange': 'In Home Care Change',
    'EntityOwner': 'Entity Owner',
    'BranchOperation': 'Branch Operation',
    'IHCAdministratorApplication': 'Administrator Application',
    'IHCProfessionalExperience': 'Professional Experience',
    'IHCManagementExperience': 'Management Experience',
    'Classification': 'Classification',
    'Nurse': 'Nurse',
    'Parent': 'Parent',
    'Address': 'Physical Address'
  };

  /**
   * Classification Types
   */
  public limited: string = 'Limited';
  public basic: string = 'Basic';
  public intermediate: string = 'Intermediate';
  public comprehensive: string = 'Comprehensive';

  /**
   * Change Types
   */
  public add: string = 'Add';
  public change: string = 'Change';
  public delete: string = 'Delete';

  /*
   * Reference Document Types
   */
  public emptyBackgroundCheck: string = 'Empty Background Check Request';
  public fingerprints: string = 'Fingerprints';
  public policyAndProcedure: string = 'Policy and Procedure Memo';
  public hospitalAttestation: string = 'Hospital Attestation';
  public sicfAttestation: string = 'SICF Attestation';

  /*
   * Statuses
   */
  public started: string = "Started";
  public submitted: string = "Submitted";
  public submittedForApproval: string = 'Submitted for Approval';
  public resubmitted: string = "Resubmitted";
  public awaitingPayment: string = "Approved, Pending Payment and Clearance";
  public approved: string = "Approved";
  public incomplete: string = "Incomplete";
  public insufficient: string = "Insufficient";
  public underReview: string = "Under Review";
  public denied: string = "Denied";
  public revoked: string = "Revoked";
  public expired: string = "Expired";
  public withdrawn: string = "Withdrawn";
  public surrendered: string = "Surrendered";
  public closed: string = 'Closed';
  public renewal: string = 'Renewal';

  userProfile: IUserProfile = {
    userId: null,
    fullName: null,
    email: null,
    currentPage: null,
    currentStep: null,
    theme: environment.theme,
    roles: []
  }

  stateList: string[] = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL",
    "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT",
    "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
    "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "American Samoa",
    "Commonwealth of the Northern Mariana Islands", "Guam", "Puerto Rico", "US Virgin Islands",
    "Federated States of Micronesia", "Republic of the Marshall Islands", "Republic of Palau"
  ];

  oregonCounties: string[] = [
    "Baker", "Benton", "Clackamas", "Clatsop", "Columbia", "Coos", "Crook",
    "Curry", "Deschutes", "Douglas", "Gilliam", "Grant", "Harney", "Hood River",
    "Jackson", "Jefferson", "Josephine", "Klamath", "Lake", "Lane", "Lincoln",
    "Linn", "Malheur", "Marion", "Morrow", "Multnomah", "Polk", "Sherman",
    "Tillamook", "Umatilla", "Union", "Wallowa", "Wasco", "Washington", "Wheeler",
    "Yamhill"
  ];

  washingtonCounties: string[] = [
    "Adams", "Asotin", "Benton", "Chelan", "Clallam", "Clark", "Columbia",
    "Cowlitz", "Douglas", "Ferry", "Franklin", "Garfield", "Grant", "Grays Harbor",
    "Island", "Jefferson", "King", "Kitsap", "Kittitas", "Klickitat", "Lewis",
    "Lincoln", "Mason", "Okanogan", "Pacific", "Pend Oreille", "Pierce", "San Juan",
    "Skagit", "Skamania", "Snohomish", "Spokane", "Stevens", "Thurston", "Wahkiakum",
    "Walla Walla", "Whatcom", "Whitman", "Yakima"
  ];

  idahoCounties: string[] = [
    "Ada", "Adams", "Bannock", "Bear Lake", "Benewah", "Bingham", "Blaine",
    "Boise", "Bonner", "Bonneville", "Boundary", "Butte", "Camas", "Canyon",
    "Caribou", "Cassia", "Clark", "Clearwater", "Custer", "Elmore", "Franklin",
    "Fremont", "Gem", "Gooding", "Idaho", "Jefferson", "Jerome", "Kootenai",
    "Latah", "Lemhi", "Lewis", "Lincoln", "Madison", "Minidoka", "Nez Perce",
    "Oneida", "Owyhee", "Payette", "Power", "Shoshone", "Teton", "Twin Falls",
    "Valley", "Washington"
  ];

  californiaCounties: string[] = [
    "Alameda", "Alpine", "Amador", "Butte", "Calaveras", "Colusa", "Contra Costa",
    "Del Norte", "El Dorado", "Fresno", "Glenn", "Humboldt", "Imperial", "Inyo",
    "Kern", "Kings", "Lake", "Lassen", "Los Angeles", "Madera", "Marin", "Mariposa",
    "Mendocino", "Merced", "Modoc", "Mono", "Monterey", "Napa", "Nevada", "Orange",
    "Placer", "Plumas", "Riverside", "Sacramento", "San Benito", "San Bernardino",
    "San Diego", "San Francisco", "San Joaquin", "San Luis Obispo", "San Mateo",
    "Santa Barbara", "Santa Clara", "Santa Cruz", "Shasta", "Sierra", "Siskiyou",
    "Solano", "Sonoma", "Stanislaus", "Sutter", "Tehama", "Trinity", "Tulare",
    "Tuolumne", "Ventura", "Yolo", "Yuba"
  ];

  otherStateCounties: string[] = ["Other"];

  countiesByState: { [key: string]: string[] } = {
    "OR": this.oregonCounties,
    "WA": this.washingtonCounties,
    "ID": this.idahoCounties,
    "CA": this.californiaCounties,
  };
  /**JJ - Get counties for a given state. Returns specific county lists for OR, WA, ID, CA
   * and otherStateCounties for all other states. CoPilot help.
   */
  getCountiesForState(state: string): string[] {
    return this.countiesByState[state] || this.otherStateCounties;
  }

  loremIpsum: string = `Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor
  incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
  laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit
  esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt
  in culpa qui officia deserunt mollit anim id est laborum.`

  public confirmDialogRef: MatDialogRef<ConfirmDialogComponent> = null!;

  private httpClient = inject(HttpClient);
  public dialog = inject(MatDialog);
  public toastr = inject(ToastrService);

  public openNotice(message: string): void {
    let data: IConfirmDialog = {
      message: message,
      confirmation: false,
      darkMode: this.darkMode
    }
    this.openDialog(data);
  }

  public openConfirm(message: string): void {
    let data: IConfirmDialog = {
      message: message,
      confirmation: true,
      darkMode: this.darkMode
    }
    this.openDialog(data);
  }

  openDialog(data: IConfirmDialog): void {
    this.confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: data,
      role: 'dialog',
      ariaLabel: "Confirmation Dialog",
      minWidth: '300px',
      maxWidth: !this.mobile ? '700px' : '300px',
      maxHeight: this.mobile ? '600px' : '700px',

    });
  }

  public confirmed(): Observable<any> {
    return this.confirmDialogRef.afterClosed().pipe(take(1), map(response => {
      return response;
    }));
  }

  requestChange(dialogData: ILicenseChangeDialog): void {
    this.dialog.open(InHomeCareChangeDialogComponent, {
      data: dialogData,
      role: 'dialog',
      ariaLabel: "Change Request Dialog",
      minWidth: !this.mobile ? '800px' : '',
      maxWidth: !this.mobile ? '1000px' : '300px',

    });
  }

  get isMobile(): boolean {
    return this.mobile;
  }

  get counties(): string[] {
    return this.oregonCounties;
  }

  get states(): string[] {
    return this.stateList;
  }

  themeChanged$ = this.changeTheme.asObservable();

  setUserTheme(): void {
    if (this.userProfile.userId === null) {
      this.getUserInfo().subscribe({
        next: response => this.userProfile = response,
        error: e => console.log('error getting user info', e),
        complete: () => this.toggleTheme()
      });
    }
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text);
    this.toastr.success("Copied!");
  }

  toggleTheme() {
    this.userProfile.theme = resolveTheme(this.userProfile.theme).id;
    this.changeTheme.next(this.userProfile.theme);
  }

  updateUserProfileState(): void {
    this.loading = true;
    this.httpClient.post<IUserProfile>(this.api + "UserProfile/UpdateUserProfileState", this.userProfile).subscribe(
      () => { this.loading = false; },
      error => {
        this.consoleLog(error);
        this.loading = false;
      }
    );
  }

  getUserInfo(): Observable<IUserProfile> {
    return this.httpClient.get<IUserProfile>(this.api + "UserProfile/GetUserProfile");
  }

  consoleLog(error: any): void {
    if (this.development) {
      console.log('error', error);
    }
  }

  isCancelled(response: any): boolean {
    return response === 'cancel' || response === null || response === '' || response === undefined;
  }

  getDateFromString(date: string): Date {
    const dateComponents = date.split('/');
    const month = parseInt(dateComponents[0], 10);
    const day = parseInt(dateComponents[1], 10);
    const year = parseInt(dateComponents[2], 10);
    return new Date(year, month - 1, day);
  }
}
